import type { EffectivePermissions } from '../utils/auth-permissions';

// API Auth Types согласно документации
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  register_token: string;
  personal_data_consent: boolean;
}

export interface AdminRegisterRequest {
  email: string;
  password?: string;
  role?: 'business' | 'business_user' | 'admin' | 'root' | 'manager';
  register_token?: string;
  personal_data_consent?: boolean;
  referral_code?: string;
  data_id?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  role?: string;
  business_status?: string;
  registration_stage?: 'INITIAL_REGISTRATION' | 'DATA_PROVIDED' | 'ACCOUNT_CREATED';
}

export interface CurrentUser {
  id?: string;
  user_id?: string;
  email?: string;
  role: string;
  /**
   * BE-800: эффективные права пользователя. У старых сохранённых профилей поля нет —
   * такой случай означает «права ещё не известны», а не «прав нет» (см. auth-permissions).
   */
  permissions?: EffectivePermissions;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface LogoutRequest {
  refresh_token: string;
  access_token: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetResponse {
  message: string;
  reset_link?: string | null;
}

export interface PasswordResetConfirmRequest {
  reset_token: string;
  new_password: string;
}

// Business Data Types - соответствует NewBusinessDataRequestSchema из API
export type ApiOrganizationType = 'INDIVIDUAL' | 'SELF_EMPLOYED' | 'LEGAL_ENTITY';

export interface BusinessDataRequest {
  // Обязательные поля для всех типов
  company_type: ApiOrganizationType;
  sphere: string;
  phone: string;
  email: string;
  inn?: string;
  address?: string;
  organization_name?: string;
  city?: string;
  organization_description?: string;
  bank_number: string;
  bank_id_code: string;
  bank_name: string;
  correspondent_account: string;

  // Опциональные поля
  full_name?: string;
  ogrn?: string;
  passport_series?: string;
  passport_number?: string;
  issued_by?: string;
  issued_at?: string;
  issuer_code?: string;
}

export interface BusinessProfile {
  id: string;
  userId: string;
  organizationType: OrganizationType;
  isRegistered: boolean;
  registrationData?: RegistrationData;
  createdAt: Date;
  updatedAt: Date;
}

export type OrganizationType = 'self_employed' | 'individual' | 'legal_entity';

// Маппинг OrganizationType в значения для API (API требует верхний регистр)
export const ORGANIZATION_TYPE_TO_API_MAP: Record<OrganizationType, ApiOrganizationType> = {
  self_employed: 'SELF_EMPLOYED',
  individual: 'INDIVIDUAL',
  legal_entity: 'LEGAL_ENTITY'
};

export interface RegistrationData {
  // Обязательные поля для всех типов
  company_type: OrganizationType;
  sphere: string;
  phone: string;
  email: string;
  inn?: string;
  address?: string;
  organization_name?: string;
  city?: string;
  organization_description?: string;
  bank_number: string;
  bank_id_code: string;
  bank_name: string;
  correspondent_account: string;

  // Опциональные поля
  full_name?: string;
  ogrn?: string;
  passport_series?: string;
  passport_number?: string;
  issued_by?: string;
  issued_at?: string;
  issuer_code?: string;
}

export interface AuthState {
  // Бизнес-пользователь кабинета (тот же shape, что и currentUser — GET /auth/me).
  // Заполняется только email/password-флоу; на живых путях всегда null.
  user: CurrentUser | null;
  currentUser: CurrentUser | null;
  businessProfile: BusinessProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tokens: TokenResponse | null;
}

export interface ClientRegistrationData {
  fullName: string;
  phone: string;
  email: string;
  country: string;
  city: string;
  organizationName: string;
  organizationDescription: string;
  discountSize: string;
  comment: string;
  preferredContactPhone?: string;
  preferredContactEmail?: string;
  preferredContactWhatsApp?: string;
  preferredContactTelegram?: string;
}

export interface User {
  id: string;
  email: string;
  role: string;
  created_at: string;
  deleted_at: string | null;
  last_password_reset_at: string | null;
  tokens_revoked_at: string | null;
  last_signed_at?: string | null;
  last_signed_ip?: string | null;
  current_signed_at?: string | null;
  current_signed_ip?: string | null;
  /** Признак блокировки (для игроков). В /auth/users не приходит; оставлено для будущих табов. */
  banned?: boolean | null;
  /** BE-765: ID связанного бизнеса (owner/employee). Бэкенд-дополнение к /auth/users. */
  business_id?: string | null;
  /** BE-765: название связанного бизнеса. Пока не приходит от бэкенда → placeholder «—». */
  business_name?: string | null;
}

export interface UsersListResponse {
  users: User[];
  total: number;
}

/**
 * BE-1242: строка справочника внутренних пользователей (GET /admin/users).
 * Владельцы бизнесов и их сотрудники сюда не попадают ни при каком фильтре.
 */
export interface InternalUser {
  id: string;
  email: string;
  role: string;
  created_at: string;
  deleted_at?: string | null;
  last_signed_at?: string | null;
  /**
   * Сколько прав выдано. Считаются только живые ключи каталога, поэтому число
   * совпадает с количеством включённых тумблеров в редакторе прав.
   */
  permissions_count?: number;
}

/** BE-1242: ответ GET /admin/users. На пустой странице items_from и items_to равны 0. */
export interface InternalUsersListResponse {
  items: InternalUser[];
  total: number;
  items_from: number;
  items_to: number;
}

export interface Manager {
  id: string;
  email: string;
  role: string;
  created_at: string;
  deleted_at: string | null;
  last_password_reset_at: string | null;
  tokens_revoked_at: string | null;
  last_signed_at: string | null;
  last_signed_ip: string | null;
  current_signed_at: string | null;
  current_signed_ip: string | null;
  assigned_businesses_count: number;
  /**
   * BE-1249: начислять ли менеджеру вознаграждение 2 % по заказам закреплённых за ним
   * бизнесов. Признак персональный и по умолчанию выключен; на стендах без бэковой
   * части эпика поля в ответе нет — отсутствие читаем как «выключено».
   */
  manager_reward_enabled?: boolean;
}

export interface ManagersListResponse {
  managers: Manager[];
  total: number;
  items_from: number;
  items_to: number;
}

/** Ответственный менеджер бизнеса. ФИО бэк не хранит — подпись по email. */
export interface AssignedManagerRef {
  id: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
}

/** POST /admin/businesses/{id}/assign_manager; `assigned_manager_id: null` — менеджер откреплён. */
export interface AssignManagerResponse {
  business_id: string;
  assigned_manager_id: string | null;
  assigned_manager?: AssignedManagerRef | null;
}

export interface InviteManagerRequest {
  email: string;
}

/** Тело POST /admin/accountants/invite — приглашение бухгалтера. */
export interface InviteAccountantRequest {
  email: string;
}

/** Ответ POST /admin/managers/invite (201). */
export interface InviteManagerResponse {
  token?: string;
  email: string;
  expires_at?: string;
  registration_link?: string;
}

/** Бизнес в карточке менеджера (GET /admin/managers/{id}). */
export interface ManagerAssignedBusiness {
  id: string;
  organization_name: string;
  inn: string;
  ogrn: string;
  phone: string;
  email: string;
}

/** Ответ GET /admin/managers/{manager_id} — детали менеджера с закреплёнными бизнесами. */
export interface ManagerDetail extends Omit<Manager, 'assigned_businesses_count'> {
  assigned_business_count: number;
  assigned_businesses: ManagerAssignedBusiness[];
}

/** BE-1251: строка истории начислений кошелька менеджера. */
export interface ManagerWalletTransaction {
  /** Сумма начисления, приходит строкой (Decimal). */
  amount: string;
  /** Тип операции строкой; словарь подписей на фронте, незнакомое печатаем как есть. */
  type: string;
  /** Расчётный период начисления; у части строк может отсутствовать. */
  period_year?: number | null;
  period_month?: number | null;
  created_at: string;
}

/** Ответ GET /admin/managers/{manager_id}/wallet — баланс и страница начислений. */
export interface ManagerWalletResponse {
  balance: string;
  /** Блокировки средств у менеджера сейчас нет — на экран не выводим. */
  locked_balance: string;
  updated_at?: string | null;
  items: ManagerWalletTransaction[];
  total: number;
  items_from: number;
  items_to: number;
}

export interface ManagerNoteResponse {
  manager_note: string | null;
}

/** Тип компании в POST /businesses/register (верхний регистр). */
export type BusinessDraftCompanyType = 'INDIVIDUAL' | 'SELF_EMPLOYED' | 'LEGAL_ENTITY';

/** Запрос на создание черновика бизнеса: POST /businesses/register (без auth). */
export interface BusinessRegistrationDraftRequest {
  full_name: string;
  organization_name: string;
  email: string;
  phone?: string | null;
  invite_code?: string | null;
  company_type?: BusinessDraftCompanyType;
  inn?: string;
  ogrn?: string;
  city?: string;
  address?: string;
}

/** Ответ POST /businesses/register (черновик). */
export interface BusinessRegistrationDraftResponse {
  id: string;
  token: string;
  email: string;
  expires_at: string;
  registration_link: string;
}

/** Тело PUT /businesses/{business_id} — обновление профиля бизнеса (все поля опциональны). */
export interface BusinessUpdateDataRequest {
  organization_name?: string | null;
  organization_description?: string | null;
  sphere?: string | null;
  picture_url?: string | null;
  picture_key?: string | null;
  discount_percent?: number | null;
  discount_desc?: string | null;
  country?: string | null;
  city?: string | null;
  address?: string | null;
  full_name?: string | null;
  phone?: string | null;
  email?: string | null;
  preferred_contact_method?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  /** @deprecated читать contact_phone */
  additional_phone?: string | null;
  whatsapp_number?: string | null;
  /** @deprecated читать contact_email */
  additional_email?: string | null;
  telegram_username?: string | null;
  comment?: string | null;
  company_type?: string | null;
  inn?: string | null;
  ogrn?: string | null;
  bank_number?: string | null;
  bank_id_code?: string | null;
  bank_name?: string | null;
  correspondent_account?: string | null;
  passport_series?: string | null;
  passport_number?: string | null;
  issued_by?: string | null;
  issued_at?: string | null;
  issuer_code?: string | null;
  // BE-1032: заверение о полномочиях.
  authority_basis?: string | null;
  power_of_attorney_number?: string | null;
  /** ISO-дата 'YYYY-MM-DD' (бэк принимает также 'DD.MM.YYYY'). */
  power_of_attorney_date?: string | null;
  authority_other?: string | null;
}

/**
 * BE-576: черновик витрины бизнеса (без отправки на модерацию).
 * GET/PUT/DELETE /businesses/{id}/marketplace/draft.
 */
export interface BusinessMarketplaceDraft {
  marketplace_name?: string | null;
  picture_key?: string | null;
  picture_url?: string | null;
  updated_at?: string | null;
}

/** BE-576: тело PUT-запроса черновика витрины. Оба поля опциональны, но хотя бы одно обязательно. */
export interface BusinessMarketplaceDraftUpdate {
  marketplace_name?: string | null;
  picture_key?: string | null;
}

/** PUT /businesses/{id}/marketplace — ответ (BusinessMarketplaceInfoUpdateResponseSchema). */
export interface BusinessMarketplaceUpdateResponse {
  id: string;
  organization_name?: string | null;
  organization_description?: string | null;
  picture_url?: string | null;
  rating?: number | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  pending_update_id?: string | null;
}

// Initial Registration Types
export interface InitialRegistration {
  id: string; // Это же значение используется как data_id в эндпоинтах одобрения/отклонения
  full_name: string;
  phone: string;
  email: string;
  country?: string;
  city: string;
  organization_name: string; // API возвращает organization_name, не organization_name
  organization_description: string; // API возвращает organization_description, не organization_description
  discount_desc: string;
  comment?: string;
  additional_phone?: string;
  additional_email?: string;
  whatsapp_number?: string;
  telegram_username?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ApproveInitialRegistrationResponse {
  token: string;
  data_id: string;
  email: string;
  expires_at: string;
}

export interface RejectInitialRegistrationResponse {
  email: string;
  reason: string;
}

/** GET /auth/invitation?token=... — данные приглашения сотрудника.
 *  business_name по OpenAPI (BusinessInvitationResponseSchema): nullable и не required. */
export interface AuthInvitationPreview {
  email: string;
  business_name?: string | null;
  business_user_role: 'OWNER' | 'MANAGER' | string;
}

/** Единый запрос регистрации по инвайт-токену: POST /auth/register (менеджер, бизнес и т.д.). */
export interface RegisterWithTokenRequest {
  email: string;
  password: string;
  register_token: string;
  personal_data_consent: boolean;
}

/** Ответ POST /auth/register — токены для авторизации. */
export interface RegisterWithTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  role: string;
  user_id?: string;
  business_status?: string;
  registration_stage?: 'INITIAL_REGISTRATION' | 'DATA_PROVIDED' | 'ACCOUNT_CREATED';
}

export interface BusinessListItem {
  id: string;
  owner_id: string | null;
  name: string | null;
  inn: string | null;
  city: string | null;
  business_status: 'ACTIVE' | 'BANNED' | 'ON_MODERATION' | 'CANCELED' | 'NO_DATA';
  profile_completion_percent: number;
  has_active_promotions: boolean;
  created_at: string;
  lead_source: string | null;
  manager_note: string | null;
  icon_url: string | null;
  assigned_manager_id: string | null;
  assigned_manager_name: string | null;
}

/**
 * BE-1379: фильтры списка бизнесов. Вынесены из сигнатуры метода, потому что теперь их
 * два потребителя: `getBusinesses` (массив) и `getBusinessesPage` (конверт с total).
 */
export interface AdminBusinessesListParams {
  q?: string;
  owner_id?: string;
  company_type?: string;
  has_active_promotions?: boolean;
  integration_type?: string;
  assigned_manager_id?: string;
  assigned_manager_is_null?: boolean;
  order_by?: string;
  order_desc?: boolean;
  limit?: number;
  offset?: number;
  status?: string;
}

export interface BusinessesListResponse {
  items: BusinessListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface BusinessUserListItem {
  id: string;
  user_id: string;
  email: string;
  role: 'OWNER' | 'MANAGER';
  last_login_at?: string;
}

/** Элемент списка своих бизнесов (GET /businesses) — для выбора кабинета. */
export interface UserBusinessItem {
  id: string;
  business_id?: string; // альтернативное поле с бэкенда
  organization_name: string;
  name?: string; // альтернативное название
  role: 'owner' | 'employee';
  status: BusinessStatus;
}

export type BusinessStatus = 'NO_DATA' | 'ON_MODERATION' | 'ACTIVE' | 'BANNED' | 'CANCELED';

/** Ответ POST /businesses/{business_id}/send_to_moderation — BusinessStatusResponseSchema. */
export interface BusinessStatusResponse {
  business_id: string;
  status: BusinessStatus;
}

/** BE-1145: кем приглашён бизнес — агентом реферальной системы или сотрудником платформы. */
export type ReferrerType = 'AGENT' | 'AUTHORIZED_USER';

export interface FullBusinessDetails {
  id: string;
  status: BusinessStatus;
  status_updated_at?: string | null;
  discount_percent: number;
  email: string;
  created_at: string;
  list_users: BusinessUserListItem[];
  profile_completion_percentage: number;
  has_active_promotions: boolean;
  assigned_manager_id?: string | null;
  /** BE-1531: ответственный менеджер. Если бэк не нашёл пользователя, приходит только id. */
  assigned_manager?: AssignedManagerRef | null;
  /** BE-1145: кто пригласил бизнес. При AGENT — id агента, при AUTHORIZED_USER — id сотрудника. */
  invited_by_id?: string | null;
  /**
   * BE-1145: тип пригласившего. Может быть пустым при заполненном invited_by_id —
   * это легаси-записи, по которым бэк начисляет долю пригласившего агента.
   */
  invited_by_type?: ReferrerType | null;
  manager_notes?: string | null;
  country?: string | null;
  city?: string | null;
  organization_name?: string | null;
  marketplace_name?: string | null;
  organization_description?: string | null;
  company_type?: string | null;
  sphere?: string | null;
  phone?: string | null;
  inn?: string | null;
  address?: string | null;
  full_name?: string | null;
  ogrn?: string | null;
  passport_series?: string | null;
  passport_number?: string | null;
  issued_by?: string | null;
  issued_at?: string | null;
  issuer_code?: string | null;
  bank_number?: string | null;
  bank_id_code?: string | null;
  bank_name?: string | null;
  correspondent_account?: string | null;
  picture_url?: string | null;
  discount_desc?: string | null;
  preferred_contact_method?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  /** @deprecated читать contact_phone */
  additional_phone?: string | null;
  whatsapp_number?: string | null;
  /** @deprecated читать contact_email */
  additional_email?: string | null;
  telegram_username?: string | null;
  comment?: string | null;
  // BE-1032: заверение о полномочиях (кабинет + админ-карточка).
  authority_basis?: string | null;
  power_of_attorney_number?: string | null;
  /** ISO-дата 'YYYY-MM-DD'. */
  power_of_attorney_date?: string | null;
  authority_other?: string | null;
  // BE-1032: акцепт оферты (отдаётся только в админ-карточке).
  offer_accepted_at?: string | null;
  offer_version?: string | null;
}

/**
 * BE-824: статус привязки личного Telegram внутреннего пользователя.
 * `GET /admin/me/telegram`. Пока не привязан — username и linked_at приходят null.
 */
export interface TelegramLinkStatus {
  is_linked: boolean;
  username?: string | null;
  linked_at?: string | null;
}

/**
 * BE-824: выданный одноразовый код привязки. `POST /admin/me/telegram/link`.
 * Выдача гасит прежние неиспользованные коды пользователя, поэтому открытая ранее
 * ссылка перестаёт работать.
 */
export interface IssuedTelegramLinkCode {
  code: string;
  /** Ссылка вида https://t.me/<бот>?start=<код> — имя бота задаётся на сервере. */
  deep_link: string;
  expires_at: string;
}
